import React, { FunctionComponent, useCallback, useState } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import { TrainingSettingsTypeEnum } from '@halter-corp/training-service-client';

import { Modifier, Profile } from 'data/training';

import { useProfilesByType, useModifiersByType } from '../hooks/use-profiles-by-type';
import { prettyPrintProfileType } from '../utils';
import ProfilesTable from './profiles-table';
import ModifiersTable from './modifiers-table';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

const FarmTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const ProfilesWrapper = styled.div`
  padding: 8px;
`;

export const TypeTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  opacity: 0.75;
  padding: 4px;
`;

const ModifiersWrapper = styled.div`
  padding-top: 8px;
`;

export const ModifierTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  opacity: 0.6;
  padding: 4px;
`;

type FarmProfileProps = {
  farmName: string;
  profiles: Profile[];
  modifiers: Modifier[];
  onSelectProfile: (profile: Profile) => void;
  onSelectModifier: (modifier: Modifier) => void;
};

const SESSION_KEY = (farmName: string) => `training-accordion-expanded:${farmName}`;

const readExpandedState = (farmName: string): Record<string, boolean> => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY(farmName));
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const FarmProfiles: FunctionComponent<FarmProfileProps> = ({
  farmName,
  profiles,
  modifiers,
  onSelectProfile,
  onSelectModifier,
}) => {
  const profilesByType = useProfilesByType(profiles);
  const modifiersByType = useModifiersByType(modifiers);

  const [expandedState, setExpandedState] = useState<Record<string, boolean>>(() =>
    readExpandedState(farmName)
  );

  const handleExpandedChange = useCallback(
    (key: string, isExpanded: boolean) => {
      setExpandedState((prev) => {
        const next = { ...prev, [key]: isExpanded };
        try {
          sessionStorage.setItem(SESSION_KEY(farmName), JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });
    },
    [farmName]
  );

  return (
    <Wrapper>
      <FarmTitle>{farmName}</FarmTitle>
      {Object.values(TrainingSettingsTypeEnum).map((type) => {
        const profilesForType = profilesByType[type] ?? [];
        const modifiersForType = modifiersByType[type] ?? [];

        if (isEmpty(profilesForType) && isEmpty(modifiersForType)) return null;
        return (
          <ProfilesWrapper key={type}>
            <ProfilesTable
              profiles={profilesForType}
              onPressRow={onSelectProfile}
              title={prettyPrintProfileType(type as TrainingSettingsTypeEnum)}
              expanded={expandedState[`${type}-profiles`] ?? false}
              onExpandedChange={(isExpanded) => handleExpandedChange(`${type}-profiles`, isExpanded)}
            />
            <ModifiersWrapper>
              <ModifiersTable
                modifiers={modifiersByType[type] ?? []}
                onPressRow={onSelectModifier}
                title={prettyPrintProfileType(type as TrainingSettingsTypeEnum)}
                expanded={expandedState[`${type}-modifiers`] ?? false}
                onExpandedChange={(isExpanded) => handleExpandedChange(`${type}-modifiers`, isExpanded)}
              />
            </ModifiersWrapper>
          </ProfilesWrapper>
        );
      })}
    </Wrapper>
  );
};

export default FarmProfiles;
